<template>
  <div
    class="sr-profile"
    data-test="graphics-sportradarAddDataSettings"
  >
    <div class="sr-profile__content">
      <div class="sr-profile__left sportradar-mapper">
        <div class="sportradar-mapper__scrollable">
          <p class="sr-profile__small-title margin-top-lg">
            Data You Selected
          </p>
          <div
            v-for="(s, idx) in selectedData"
            :key="s.key"
            class="sr-profile__selected-data"
          >
            <button
              class="btn btn-sm btn-link sr-profile__selected-data-remove"
              :data-test="`graphics-sportradarAddDataSettings-remove-${s.key}`"
              @click="removeKey(idx)"
            >
              <span class="fas fa-times"></span>
            </button>
            <div>
              <p class="sr-profile__selected-data-key">
                {{ $filters.snakeToCapitalized(s.key) }}
              </p>
              <p
                v-if="
                  advancedOptions[s.path] && advancedOptions[s.path].syncLabel
                "
                class="sr-profile__selected-data-key"
              >
                with label
              </p>
            </div>
          </div>
        </div>
      </div>
      <div class="sr-profile__right sportradar-mapper">
        <p class="sr-profile__small-title margin-top-lg">
          Select your data points for your template.
        </p>
        <div class="google-connect-form__search margin-bottom-md">
          <input
            v-model="searchTerm"
            type="text"
            class="mx-input"
            placeholder="Search..."
            data-test="graphics-sportradarAddDataSettings-search"
          >
          <span class="fas fa-search" />
        </div>
        <div class="sportradar-mapper__scrollable">
          <key-mapper
            v-model="selectedKeys"
            :data="formattedData"
            multiple
            :force-expand="!!searchTerm"
          >
            <template #multiple="{ path, label }">
              <div class="flex-space-between flex-align-center flex-grow">
                <label class="checkbox key-mapper__checkbox">
                  <input
                    v-model="selectedKeys"
                    type="checkbox"
                    :value="path"
                    data-test="adddata-checkbox-key"
                  >
                  {{ label }}
                  <div class="checkmark">
                    <span class="checkmark-tick fa-solid fa-check" />
                  </div>
                </label>

                <button
                  v-if="type === 'text'"
                  class="sr-link-toggle"
                  :class="{
                    'sr-link-toggle--on':
                      advancedOptions[path] && advancedOptions[path].syncLabel,
                  }"
                  data-test="graphics-sportradarAddDataSettings-linkLabel"
                  data-entity="graphics-sportradarAddDataSettings-linkLabel"
                  @click="toggleSyncLabel(path)"
                >
                  {{ advancedOptions[path] && advancedOptions[path].syncLabel ? 'Unlink' : 'Link' }}
                  Label
                </button>
              </div>
            </template>
          </key-mapper>
        </div>
      </div>
    </div>
    <div class="flex flex-space-between">
      <button
        class="btn btn-link"
        data-test="graphics-sportradarAddDataSettings-cancel"
        data-entity="graphics-sportradarAddDataSettings-cancel"
        @click="$emit('close')"
      >
        Cancel
      </button>
      <button
        class="btn btn-style-a"
        :disabled="!selectedKeys.length"
        data-test="graphics-sportradarAddDataSettings-submit"
        data-entity="graphics-sportradarAddDataSettings-submit"
        @click="onSubmit"
      >
        Add Data
      </button>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { pick, get, isString } from 'lodash-es';
import Fuse from 'fuse.js';
import { snakeToCapitalized } from '../../../../utils/filters';

import sportradarMixin from '../../../../mixins/sportradar-mixin';

import KeyMapper from './KeyMapper.vue';

import flatten from 'flat';

const { unflatten } = flatten;

export default {
  emits: ['close', 'map-many', 'update:title'],
  name: 'SportradarAddData',
  components: {
    KeyMapper,
  },
  mixins: [sportradarMixin],
  props: {
    dataProvider: {
      required: true,
    },
    element: {
      required: true,
    },
    type: {
      required: false,
      default: 'text',
    },
    title: {
      type: String,
    },
  },
  data() {
    return {
      modalTitle: 'Add Additional Data',
      selectedKeys: [],
      searchTerm: '',
      advancedOptions: {},
    };
  },
  computed: {
    ...mapState({
      getDataForElement(state, getters) {
        return getters[`${this.dataProvider.id}/getDataForElement`];
      },
      abstractionKeys(state, getters) {
        return getters[`${this.element.id}/abstractionKeys`];
      },
      dataFilters(state, getters) {
        return getters[`${this.element.id}/dataFilters`];
      },
    }),
    relevantData() {
      const data = this.getDataForElement(this.type) || {};

      if (this.type === 'image' && data.images) {
        const providerId = this.dataProvider?.id;
        const hasLogoPermission = providerId
          ? this.$store.getters[`${providerId}/hasLogoPermission`]
          : false;
        const hasHeadshotPermission = providerId
          ? this.$store.getters[`${providerId}/hasHeadshotPermission`]
          : false;

        const filteredImages = { ...data.images };
        if (!hasLogoPermission) {
          delete filteredImages.teamLogo;
        }
        if (!hasHeadshotPermission) {
          delete filteredImages.playerHeadshot;
        }

        return {
          ...data,
          images: filteredImages,
        };
      }

      return data;
    },
    searchedData() {
      if (this.searchTerm) {
        // Do search
        const flatData = flatten(this.relevantData);
        const keys = new Fuse(Object.keys(flatData), {
          threshold: 0.5,
          ignoreLocation: true,
        })
          .search(this.searchTerm)
          .map((r) => r.item);
        const searchedObj = pick(flatData, keys);
        return unflatten(searchedObj);
      }

      return this.relevantData;
    },
    formattedData() {
      return this.formatForKeyMapper(
        this.searchedData,
        this.relevantData,
        this.dataFilters,
      );
    },
    selectedData() {
      return this.selectedKeys.map((selectedKey) => ({
        key: selectedKey.split('.').pop(),
        path: selectedKey,
      }));
    },
  },
  methods: {
    ...mapActions('graphicsTemplate', ['setElementGroupMapById']),
    updateAdvancedOptions(key, prop, value) {
      if (!this.advancedOptions[key]) {
        this.advancedOptions[key] = {};
      }
      this.advancedOptions[key][prop] = value;
    },
    toggleSyncLabel(key) {
      const currentValue = this.advancedOptions[key]?.syncLabel || false;
      this.updateAdvancedOptions(key, 'syncLabel', !currentValue);
    },
    onSubmit() {
      const settingsArr = this.selectedKeys.map((key) => {
        let elementType = this.type;
        const value = get(this.sportradarData, key);

        // Use date element for strings that parse as dates
        if (elementType === 'text' && isString(value) && !isNaN(Date.parse(value))) {
          elementType = 'date';
        }

        let mappedProperty;
        switch (elementType) {
          case 'image':
            mappedProperty = 'imageUrl';
            break;
          default:
            mappedProperty = 'text';
            break;
        }

        const settings = {
          elementType,
          name: snakeToCapitalized(key.split('.').pop()),
          styles: {
            top: 0,
            left: 0,
          },
          toTop: true,
          data: {
            dataKeyMap: {
              [mappedProperty]: this.makeAbstractPath(key, this.abstractionKeys),
            },
            provider: this.dataProvider.id,
            ...(this.advancedOptions[key]?.syncLabel && {
              linkedElement: null,
            }),
          },
          // toTop: true,
          ...(this.advancedOptions[key]?.syncLabel && { syncLabel: true }),
        };

        return settings;
      });

      this.$emit('map-many', settingsArr);
    },
    removeKey(idx) {
      this.selectedKeys.splice(idx, 1);
    },
  },
  created() {
    this.$emit('update:title', this.modalTitle);
  },
};
</script>
